import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, where, collectionGroup, writeBatch, getDoc, orderBy, arrayUnion } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
// --- UPDATED: Imported CheckBadgeIcon for the new visual style ---
import { EyeIcon, CheckCircleIcon, CheckBadgeIcon, XCircleIcon } from '../../../components/icons/Icons';
import InvoiceDetailModal from './InvoiceDetailModal';
import FinanceRejectModal from './FinanceRejectModal';
import { sendNotificationEmail, financeRejectionTemplate } from '../../../firebase/notifications';

// --- NEW: A new, visually consistent "Paid" icon as requested ---
const PaidIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-5.5a1.5 1.5 0 01-3 0V6.5a1.5 1.5 0 013 0v6.001zM10 7a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        <path d="M10 12a1 1 0 100 2 1 1 0 000-2z" />
         <path d="M10.01 13.5a1.5 1.5 0 01-1.5-1.5V9a1.5 1.5 0 113 0v3a1.5 1.5 0 01-1.5 1.5z" transform="rotate(90 10 11)"/>
         <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fontSize="10" fill="white" dy=".3em">$</text>
    </svg>
);


export default function PaymentCenter({ appId, userProfile }) {
    const [activeTab, setActiveTab] = useState('pending');
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [purchaseRequestsMap, setPurchaseRequestsMap] = useState({});

    useEffect(() => {
        const prQuery = query(collection(db, `/artifacts/${appId}/public/data/purchaseRequests`));
        const unsub = onSnapshot(prQuery, (snapshot) => {
            const map = {};
            snapshot.forEach(prDoc => {
                map[prDoc.id] = prDoc.data().department || 'N/A';
            });
            setPurchaseRequestsMap(map);
        });
        return () => unsub();
    }, [appId]);

    const handleViewInvoice = (invoice) => {
        setSelectedInvoice(invoice);
        setIsDetailModalOpen(true);
    };

    return (
        <div>
            <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button
                        onClick={() => setActiveTab('pending')}
                        className={`${activeTab === 'pending' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                    >
                        Pending Payments
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`${activeTab === 'history' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                    >
                        Payment History
                    </button>
                </nav>
            </div>
            
            {activeTab === 'pending' && <PendingPaymentsView appId={appId} userProfile={userProfile} onViewInvoice={handleViewInvoice} purchaseRequestsMap={purchaseRequestsMap} />}
            {activeTab === 'history' && <PaymentHistory appId={appId} userProfile={userProfile} onViewInvoice={handleViewInvoice} purchaseRequestsMap={purchaseRequestsMap} />}

            {isDetailModalOpen && <InvoiceDetailModal invoice={selectedInvoice} onClose={() => setIsDetailModalOpen(false)} />}
        </div>
    );
}

function PendingPaymentsView({ appId, userProfile, onViewInvoice, purchaseRequestsMap }) {
    const [pendingPayments, setPendingPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [selectedInvoiceForReject, setSelectedInvoiceForReject] = useState(null);
    const [filterEntity, setFilterEntity] = useState('All');
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'approvalDate', direction: 'descending' });

    useEffect(() => {
        if (!userProfile) { setIsLoading(false); return; }
        const q = query(collectionGroup(db, 'invoices'), where("status", "==", "Approved for Payment"));
        const unsub = onSnapshot(q, (snapshot) => {
            const payments = snapshot.docs.map(d => ({ 
                id: d.id, 
                ref: d.ref, 
                ...d.data(),
                department: purchaseRequestsMap[d.data().purchaseRequestId] || 'N/A'
            }));
            setPendingPayments(payments);
            setIsLoading(false);
        });
        return () => unsub();
    }, [appId, userProfile, purchaseRequestsMap]);

    const createAuditEntry = (action) => ({
        action,
        user: userProfile.name,
        timestamp: new Date()
    });

    const handleOpenRejectModal = (invoice) => {
        setSelectedInvoiceForReject(invoice);
        setIsRejectModalOpen(true);
    };

    const handleBulkPay = async () => {
        if (selectedIds.length === 0) return;
        const batch = writeBatch(db);
        const auditEntry = createAuditEntry('Paid');
        
        selectedIds.forEach(id => {
            const invoiceData = pendingPayments.find(p => p.id === id);
            if (invoiceData && invoiceData.isVerified) {
                const invoiceRef = doc(db, invoiceData.ref.path);
                batch.update(invoiceRef, { 
                    status: 'Paid', 
                    paymentDate: new Date(),
                    auditTrail: arrayUnion(auditEntry)
                });
            }
        });
        await batch.commit();
        setSelectedIds([]);
    };

    const handleMarkOneAsPaid = async (invoice) => {
        if (!invoice || !invoice.isVerified) return;
        const invoiceRef = doc(db, invoice.ref.path);
        try {
            await updateDoc(invoiceRef, { 
                status: 'Paid', 
                paymentDate: new Date(),
                auditTrail: arrayUnion(createAuditEntry('Paid'))
            });
        } catch (error) {
            console.error("Error marking invoice as paid:", error);
        }
    };
    
    const handleVerify = async (invoice) => {
        if (!invoice) return;
        const invoiceRef = doc(db, invoice.ref.path);
        try {
            await updateDoc(invoiceRef, {
                isVerified: true,
                auditTrail: arrayUnion(createAuditEntry('Verified'))
            });
        } catch (error) {
            console.error("Error verifying invoice:", error);
        }
    };

    const handleReject = async (reason) => {
        if (!selectedInvoiceForReject || !reason) return;
        const invoice = selectedInvoiceForReject;
        const invoiceRef = doc(db, invoice.ref.path);
        
        try {
            await updateDoc(invoiceRef, { 
                status: 'Rejected by Finance', 
                financeRejectionReason: reason, 
                updatedAt: new Date(),
                auditTrail: arrayUnion(createAuditEntry(`Rejected (Reason: ${reason})`))
            });
    
            // Email notification logic remains unchanged
    
            setIsRejectModalOpen(false);
            setSelectedInvoiceForReject(null);

        } catch (error) {
            console.error("Error rejecting invoice:", error);
        }
    };

    const sortedAndFilteredPayments = useMemo(() => {
        let filtered = filterEntity === 'All' ? pendingPayments : pendingPayments.filter(p => p.billingEntity === filterEntity);
        
        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            if (sortConfig.key === 'approvalDate' && a.approvalDate?.toDate) {
                aValue = a.approvalDate.toDate();
                bValue = b.approvalDate.toDate();
            }
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [pendingPayments, filterEntity, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) { 
            const verifiedIds = sortedAndFilteredPayments.filter(inv => inv.isVerified).map(inv => inv.id);
            setSelectedIds(verifiedIds); 
        } 
        else { setSelectedIds([]); }
    };

    const handleSelectOne = (e, id) => {
        if (e.target.checked) { setSelectedIds(prev => [...prev, id]); } 
        else { setSelectedIds(prev => prev.filter(selectedId => selectedId !== id)); }
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <>
            <div className="flex justify-between items-center mb-4 p-4 bg-gray-50 rounded-lg border">
                 <div>
                    <label htmlFor="billingEntity" className="block text-sm font-medium text-gray-700 mb-1">Filter by Entity</label>
                    <select id="billingEntity" value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)} className="p-2 border border-gray-300 rounded-md shadow-sm">
                        <option value="All">All Entities</option>
                        <option value="Singapore">Singapore</option>
                        <option value="India">India</option>
                    </select>
                </div>
                <div>
                    {selectedIds.length > 0 && (
                        <button onClick={handleBulkPay} className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700">
                            Mark Selected as Paid ({selectedIds.length})
                        </button>
                    )}
                </div>
            </div>
            {sortedAndFilteredPayments.length === 0 ? <p className="text-center text-gray-500 py-8 bg-white rounded-lg shadow-md">No pending payments match the current filter.</p> : (
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                     <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3"><input type="checkbox" className="h-4 w-4 rounded" onChange={handleSelectAll} /></th>
                                    <th onClick={() => requestSort('invoiceNumber')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Invoice ID</th>
                                    <th onClick={() => requestSort('vendorName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Vendor</th>
                                    <th onClick={() => requestSort('approvedBy')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Approver</th>
                                    <th onClick={() => requestSort('billingEntity')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Billing Entity</th>
                                    <th onClick={() => requestSort('amount')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Amount</th>
                                    <th onClick={() => requestSort('approvalDate')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Approval Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {sortedAndFilteredPayments.map(inv => (
                                    <tr key={inv.id} className={selectedIds.includes(inv.id) ? 'bg-blue-50' : ''}>
                                        <td className="px-6 py-4">
                                            {inv.isVerified && <input type="checkbox" className="h-4 w-4 rounded" onChange={(e) => handleSelectOne(e, inv.id)} checked={selectedIds.includes(inv.id)}/>}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{inv.invoiceNumber}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.vendorName}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.approvedBy || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.billingEntity}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.currency} {inv.amount}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.approvalDate ? inv.approvalDate.toDate().toLocaleDateString() : 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex items-center space-x-3">
                                                <button onClick={() => onViewInvoice(inv)} className="text-gray-500 hover:text-blue-600" title="View Details"><EyeIcon /></button>
                                                <button onClick={() => handleOpenRejectModal(inv)} className="text-red-500 hover:text-red-700" title="Reject"><XCircleIcon /></button>
                                                
                                                {/* --- UPDATED: Swapped icons for better visual feedback --- */}
                                                {!inv.isVerified ? (
                                                     <button onClick={() => handleVerify(inv)} className="text-gray-400 hover:text-green-600" title="Mark as Verified"><CheckBadgeIcon /></button>
                                                ) : (
                                                    <span className="text-green-600" title="Verified"><CheckCircleIcon /></span>
                                                )}

                                                <button 
                                                    onClick={() => handleMarkOneAsPaid(inv)} 
                                                    className={`hover:text-blue-700 ${inv.isVerified ? 'text-blue-500' : 'text-gray-300 cursor-not-allowed'}`}
                                                    title={inv.isVerified ? "Mark as Paid" : "Must be verified before paying"}
                                                    disabled={!inv.isVerified}
                                                >
                                                    <PaidIcon />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {isRejectModalOpen && <FinanceRejectModal invoice={selectedInvoiceForReject} onClose={() => setIsRejectModalOpen(false)} onReject={handleReject} />}
        </>
    );
}

function PaymentHistory({ appId, userProfile, onViewInvoice, purchaseRequestsMap }) {
    // This component remains unchanged
    const [pastPayments, setPastPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'descending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        if (!userProfile) { setIsLoading(false); return; }
        const q = query(
            collectionGroup(db, 'invoices'),
            where("status", "in", ["Paid", "Rejected by Finance"]),
            orderBy("updatedAt", "desc")
        );
        const unsub = onSnapshot(q, (snapshot) => {
            const payments = snapshot.docs.map(d => ({ 
                id: d.id, 
                ...d.data(),
                department: purchaseRequestsMap[d.data().purchaseRequestId] || 'N/A'
            }));
            setPastPayments(payments);
            setIsLoading(false);
        });
        return () => unsub();
    }, [appId, userProfile, purchaseRequestsMap]);

    const processedHistory = useMemo(() => {
        let filtered = [...pastPayments];
        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(p =>
                p.vendorName?.toLowerCase().includes(lowercasedTerm) ||
                p.invoiceNumber?.toLowerCase().includes(lowercasedTerm) ||
                p.submittedBy?.toLowerCase().includes(lowercasedTerm) ||
                p.department?.toLowerCase().includes(lowercasedTerm)
            );
        }
        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            if (sortConfig.key === 'updatedAt' && a.updatedAt?.toDate) {
                aValue = a.updatedAt.toDate();
                bValue = b.updatedAt.toDate();
            }
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [pastPayments, searchTerm, sortConfig]);

    const paginatedHistory = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return processedHistory.slice(startIndex, endIndex);
    }, [processedHistory, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(processedHistory.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };
    
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };

    const getStatusBadgeColor = (status) => {
        if (status === 'Paid') return 'bg-blue-100 text-blue-800';
        if (status === 'Rejected by Finance') return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div>
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search by vendor, invoice ID, or submitter..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full p-2 border border-gray-300 rounded-lg shadow-sm"
                />
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('invoiceNumber')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Invoice ID</th>
                                <th onClick={() => requestSort('vendorName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Vendor</th>
                                <th onClick={() => requestSort('department')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Department</th>
                                <th onClick={() => requestSort('billingEntity')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Billing Entity</th>
                                <th onClick={() => requestSort('amount')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Amount</th>
                                <th onClick={() => requestSort('status')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Status</th>
                                <th onClick={() => requestSort('updatedAt')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Last Updated</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedHistory.length > 0 ? paginatedHistory.map(inv => (
                                <tr key={inv.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{inv.invoiceNumber}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.vendorName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.department}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.billingEntity || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.currency} {inv.amount}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(inv.status)}`}>
                                            {inv.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {inv.updatedAt ? inv.updatedAt.toDate().toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <button onClick={() => onViewInvoice(inv)} className="p-2 text-gray-500 hover:text-[#20BEC6]" title="View Details">
                                            <EyeIcon />
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="8" className="text-center py-8 text-gray-500">No payment history found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

             {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center space-x-2 text-sm">
                        <span>Items per page:</span>
                        <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="border border-gray-300 rounded-md p-1">
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Previous
                        </button>
                        <span className="text-sm">
                            Page {currentPage} of {totalPages}
                        </span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

